import React from "react";

import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { graphql } from "relay-runtime";
import { Search, type SearchProps, type SearchResultData } from "semantic-ui-react";
import type { SearchResultProps } from "semantic-ui-react/dist/commonjs/modules/Search/SearchResult";

import { DueDate } from "~/common";
import Alert from "~/common/Alert";
import type { CreateOrImportProps } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import { useDebouncedValue } from "~/lib/hooks";
import { commitMutationPromise, RunQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import Avatar from "~/ui/legacy/Avatar";
import LoadingSpinner from "~/ui/LoadingSpinner";
import Tippy from "~/ui/Tooltip";

import { StateSelector } from "./selectors/StateSelector";
import { LinearActionItemIntegrationLinkWidget, linearSpecificDataNodeTypename } from "./Widget";

import type { LinearImportIssueMutation } from "~/__generated__/LinearImportIssueMutation.graphql";
import type { LinearImportIssuePreviewQuery } from "~/__generated__/LinearImportIssuePreviewQuery.graphql";
import type { LinearImportIssueSearchQuery } from "~/__generated__/LinearImportIssueSearchQuery.graphql";

import commonStyles from "~/integrations/actionItemIntegration/css/styles.module.css";
import trelloStyles from "~/integrations/trello/css/styles.module.css";

import styles from "./css/styles.module.css";

const searchQuery = graphql`
	query LinearImportIssueSearchQuery($query: String!) {
		viewer {
			integrations {
				linear {
					searchIssues(query: $query) {
						externalId
						title
						teamKey
					}
				}
			}
		}
	}
`;

const previewQuery = graphql`
	query LinearImportIssuePreviewQuery($id: String!) {
		viewer {
			integrations {
				linear {
					id
					isConnected
					organizationUrlKey
					issuePreview(issueId: $id) {
						externalId
						externalIdentifier
						name
						teamName
						due
						state {
							externalId
							name
							type
							color
						}
						allStates {
							externalId
							name
							type
							color
						}
						assignee {
							id
							fullName
							image
						}
					}
				}
			}
		}
	}
`;

const importExternalTicket = graphql`
	mutation LinearImportIssueMutation($blockKey: String!, $noteId: ID!, $initialText: String!, $destinationInfo: JSON!) {
		loadActionItemFromIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				initialText: $initialText
				destinationInfo: $destinationInfo
				integrationProvider: LINEAR
			}
		) {
			clientMutationId
		}
	}
`;

type LinearSearchIssue = NonNullable<
	NonNullable<LinearImportIssueSearchQuery["response"]["viewer"]["integrations"]["linear"]>["searchIssues"]
>[0];

type LinearPreviewIssue = NonNullable<
	NonNullable<LinearImportIssuePreviewQuery["response"]["viewer"]["integrations"]["linear"]>["issuePreview"]
>;

type LinearPreviewIssueAndUser = {
	user: Omit<
		NonNullable<LinearImportIssuePreviewQuery["response"]["viewer"]["integrations"]["linear"]>,
		"issuePreview"
	>;
	issue: LinearPreviewIssue;
};

function ResultRenderer(props: SearchResultProps) {
	const issue = props.data as LinearSearchIssue;
	return (
		<div className={trelloStyles.searchResultRow}>
			<span>{`[${issue.teamKey}] ${issue.title}`}</span>
		</div>
	);
}

function LinearIssueSearch({
	selectedIssue,
	setSelectedIssue,
}: {
	selectedIssue?: LinearSearchIssue;
	setSelectedIssue: (issue?: LinearSearchIssue) => void;
}) {
	const [search, setSearch] = React.useState("");
	const [isSearchLoading, setSearchLoading] = React.useState(false);
	const debouncedSearch = useDebouncedValue(search, 300);
	const [searchIssues, setSearchIssues] = React.useState<LinearSearchIssue[]>([]);

	const onSearchResponse = React.useCallback((response: LinearImportIssueSearchQuery["response"]) => {
		setSearchLoading(false);
		setSearchIssues((response.viewer.integrations.linear?.searchIssues || []) as LinearSearchIssue[]);
	}, []);

	const onSearchLoading = React.useCallback(() => {
		setSearchLoading(true);
	}, []);

	const onSearchChange = React.useCallback(
		(event: React.MouseEvent<HTMLElement>, data: SearchProps) => {
			const value = data.value;
			if (value !== undefined) {
				setSearch(value);
				if (selectedIssue) {
					setSelectedIssue(undefined);
				}
			}
		},
		[selectedIssue, setSelectedIssue],
	);

	const onResultSelect = React.useCallback(
		(event: React.MouseEvent<HTMLElement>, data: SearchResultData) => {
			setSearch("");
			setSelectedIssue(data.result.data);
		},
		[setSelectedIssue],
	);

	return (
		<div className={styles.rowContainer}>
			<div className={styles.label}>Linear Issue name</div>

			{debouncedSearch && (
				<RunQuery<LinearImportIssueSearchQuery>
					query={searchQuery}
					variables={{ query: debouncedSearch }}
					onResult={onSearchResponse}
					onLoading={onSearchLoading}
				/>
			)}

			<Search
				autoFocus
				icon={<i aria-hidden="true" className="dropdown icon" />}
				loading={isSearchLoading}
				placeholder="Search for a issue name"
				className={trelloStyles.search}
				results={searchIssues.map(issue => ({
					title: `[${issue.teamKey}] ${issue.title}`,
					key: issue.title,
					data: issue,
				}))}
				onSearchChange={onSearchChange}
				onResultSelect={onResultSelect}
				resultRenderer={ResultRenderer}
				showNoResults={false}
				value={search}
			/>
		</div>
	);
}

function LinearIssuePreviewDisplay({ issuePreviewAndUser }: { issuePreviewAndUser: LinearPreviewIssueAndUser }) {
	const { issue, user } = issuePreviewAndUser;
	return (
		<div className={commonStyles.preview}>
			<div className={commonStyles.row}>
				<div className={commonStyles.items}>
					<div className={commonStyles.item}>
						<div className={commonStyles.actionCheckbox} />
					</div>

					<div className={commonStyles.item}>{issue.name}</div>
				</div>

				{issue.assignee || issue.due ? (
					<div className={commonStyles.items}>
						{issue.due && (
							<div className={commonStyles.item}>
								<DueDate as="span" styled short date={moment(issue.due).format("YYYY-MM-DD")} />
							</div>
						)}

						{issue.assignee && (
							<div className={commonStyles.item}>
								<Avatar user={issue.assignee} link={false} width="24px" />
							</div>
						)}
					</div>
				) : null}
			</div>

			<div className={commonStyles.pillWrapper}>
				<LinearActionItemIntegrationLinkWidget
					actionItemLink={{
						integrationData: {
							__typename: linearSpecificDataNodeTypename,
							externalIdentifier: issue.externalIdentifier,
							teamName: issue.teamName || "",
							statusName: issue.state?.name || "",
							statusColor: issue.state?.color || "",
							linearUser: {
								id: user.id,
								isConnected: user.isConnected,
								organizationUrlKey: user.organizationUrlKey,
							},
						},
						syncStatus: "DONE_SYNCING_EXTERNALLY",
					}}
				/>
			</div>
		</div>
	);
}

function LinearIssuePreview({
	issueId,
	issuePreviewAndUser,
	setPreviewIssue,
}: {
	issueId: string;
	issuePreviewAndUser?: LinearPreviewIssueAndUser;
	setPreviewIssue: (i: LinearPreviewIssueAndUser) => void;
}) {
	const [isLoading, setIsLoading] = React.useState(false);

	const onResponse = React.useCallback(
		(response: LinearImportIssuePreviewQuery["response"]) => {
			const responseIssue = response.viewer.integrations.linear;
			if (responseIssue && responseIssue.issuePreview) {
				const { issuePreview, ...user } = responseIssue;
				setPreviewIssue({ issue: issuePreview, user });
			}
			setIsLoading(false);
		},
		[setPreviewIssue],
	);

	const onLoading = React.useCallback(() => {
		setIsLoading(true);
	}, []);

	return (
		<div>
			<RunQuery<LinearImportIssuePreviewQuery>
				query={previewQuery}
				variables={{ id: issueId }}
				onResult={onResponse}
				onLoading={onLoading}
			/>

			{isLoading && (
				<div className={styles.spinnerBody}>
					<LoadingSpinner dimmer={false} />
				</div>
			)}

			{issuePreviewAndUser && (
				<div style={{ margin: "1.25em 0" }}>
					<div className={styles.label}>Preview</div>
					<LinearIssuePreviewDisplay issuePreviewAndUser={issuePreviewAndUser} />
				</div>
			)}
		</div>
	);
}

function StateColumnsSelectors({
	teamId,
	allStates,
	uncheckedStateId,
	setUncheckedStateId,
	doneStateId,
	setDoneStateId,
	setCanSubmit,
}: {
	teamId: string;
	allStates: LinearPreviewIssue["allStates"];
	uncheckedStateId?: string;
	setUncheckedStateId: (v: string) => void;
	doneStateId?: string;
	setDoneStateId: (v: string) => void;
	setCanSubmit: CreateOrImportProps["setCanSubmit"];
}) {
	const [isTheSameState, setIsTheSameState] = React.useState(false);

	React.useEffect(() => {
		const sameState = !!uncheckedStateId && !!doneStateId && uncheckedStateId === doneStateId;
		setIsTheSameState(sameState);
		setCanSubmit(!sameState);
	}, [uncheckedStateId, doneStateId, setCanSubmit]);

	return (
		<>
			<div className={styles.flexRow}>
				<div className={styles.column}>
					<div className={styles.label}>
						Unchecked status
						<Tippy
							placement="top"
							className={styles.toolTip}
							content="When you uncheck this item, select the status that will be set in Linear"
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<StateSelector
						teamId={teamId}
						stateId={uncheckedStateId}
						setStateId={setUncheckedStateId}
						states={allStates}
						isError={isTheSameState}
					/>
				</div>

				<div className={styles.column}>
					<div className={styles.label}>
						Done status
						<Tippy
							placement="top"
							className={styles.toolTip}
							content="When you check this item, select the status that will be set in Linear"
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<StateSelector
						teamId={teamId}
						stateId={doneStateId}
						setStateId={setDoneStateId}
						states={allStates}
						isError={isTheSameState}
					/>
				</div>
			</div>

			{isTheSameState && (
				<Alert error rounded icon={faExclamationCircle}>
					You cannot specify the same list for unchecked and done
				</Alert>
			)}
		</>
	);
}

function importActionItem(
	blockKey: string,
	noteId: string,
	destinationInfo: { [val: string]: string | undefined },
	initialText: string,
) {
	return commitMutationPromise<LinearImportIssueMutation>(environment, {
		mutation: importExternalTicket,
		variables: { blockKey, noteId, destinationInfo, initialText },
	});
}

export function LinearImportIssue({
	setCanSubmit,
	registerSubmit,
	blockKey,
	noteId,
	initialText,
}: CreateOrImportProps) {
	const [selectedIssue, setSelectedIssue] = React.useState<LinearSearchIssue | undefined>();
	const [issuePreviewAndUser, setIssuePreviewAndUser] = React.useState<LinearPreviewIssueAndUser | undefined>();

	const [uncheckedStateId, setUncheckedStateId] = React.useState<string | undefined>();
	const [doneStateId, setDoneStateId] = React.useState<string | undefined>();

	const submit = React.useCallback(async () => {
		if (!selectedIssue) {
			throw new Error("Invalid state, submit should not be available if card is null");
		}
		const info = {
			/* eslint-disable camelcase */
			issue_id: selectedIssue.externalId,
			unchecked_status_id: uncheckedStateId,
			done_status_id: doneStateId,
			/* eslint-enable camelcase */
		};
		return importActionItem(blockKey, noteId, info, initialText).then(() => true);
	}, [selectedIssue, uncheckedStateId, doneStateId, blockKey, noteId, initialText]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		setCanSubmit(!!selectedIssue);
	}, [setCanSubmit, selectedIssue]);

	return (
		<div>
			<LinearIssueSearch selectedIssue={selectedIssue} setSelectedIssue={setSelectedIssue} />

			{selectedIssue && (
				<div>
					<LinearIssuePreview
						issueId={selectedIssue.externalId}
						issuePreviewAndUser={issuePreviewAndUser}
						setPreviewIssue={setIssuePreviewAndUser}
					/>

					{issuePreviewAndUser && (
						<StateColumnsSelectors
							teamId={selectedIssue.teamKey}
							allStates={issuePreviewAndUser.issue.allStates}
							uncheckedStateId={uncheckedStateId}
							setUncheckedStateId={setUncheckedStateId}
							doneStateId={doneStateId}
							setDoneStateId={setDoneStateId}
							setCanSubmit={setCanSubmit}
						/>
					)}
				</div>
			)}
		</div>
	);
}
